import { defineStore } from 'pinia';
import { createStorage } from '@/utils/Storage';
import { store } from '@/store';
import { ACCESS_TOKEN, CURRENT_USER, CURRENT_PROJECT, IS_LOCKSCREEN, CSRF_TOKEN } from '@/store/mutation-types';
import { ResultEnum } from '@/enums/httpEnum';

const Storage = createStorage({ storage: localStorage });
import { getUserInfo, login } from '@/api/generic/user';
import { storage } from '@/utils/Storage';

export interface IUserState {
  token: string;
  username: string;
  firstName: string;
  lastName: string;
  nickname: string;
  phoneNumber: string;
  faxNumber: string;
  jobTitle: string;
  welcome: string;
  avatar: string;
  userId: any;
  defaultProjectId: any;
  currentProject: any;
  projects: any[];
  permissions: any[];
  info: any;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): IUserState => ({
    token: Storage.get(ACCESS_TOKEN, ''),
    username: '',
    firstName: '',
    lastName: '',
    nickname: '',
    phoneNumber: '',
    faxNumber: '',
    jobTitle: '',
    welcome: 'Welcom to HEPS :)',
    avatar: '',
    userId: null,
    defaultProjectId: null,
    currentProject: null,
    projects: [],
    permissions: [],
    info: Storage.get(CURRENT_USER, {}),
  }),
  getters: {
    getToken(): string {
      return this.token;
    },
    getAvatar(): string {
      return this.avatar;
    },
    getUsername(): string {
      return this.username;
    },
    getFirstName(): string {
      return this.firstName;
    },
    getLastName(): string {
      return this.lastName;
    },
    getFullName(): string {
      return this.lastName + ', ' + this.firstName;
    },
    getNickname(): string {
      return this.nickname;
    },
    getPhoneNumber(): string {
      return this.phoneNumber;
    },
    getFaxNumber(): string {
      return this.faxNumber;
    },
    getJobTitle(): string {
      return this.jobTitle;
    },
    getUserId(): any {
      return this.userId;
    },
    getDefaultProjectId(): any {
      return this.defaultProjectId;
    },
    getCurrentProject(): any {
      return this.currentProject;
    },
    getProjects(): [any][] {
      return this.projects;
    },
    getPermissions(): [any][] {
      return this.permissions;
    },
    getUserInfo(): object {
      return this.info;
    },
  },
  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setAvatar(avatar: string) {
      this.avatar = avatar;
    },
    setUserame(username: string) {
      this.username = username;
    },
    setFirstName(firstName: string) {
      this.firstName = firstName;
    },
    setLastName(lastName: string) {
      this.lastName = lastName;
    },
    setNickame(nickname: string) {
      this.nickname = nickname;
    },
    setPhoneNumber(phoneNumber: string) {
      this.phoneNumber = phoneNumber;
    },
    setFaxNumber(faxNumber: string) {
      this.faxNumber = faxNumber;
    },
    setJobTitle(jobTitle: string) {
      this.jobTitle = jobTitle;
    },
    setUserId(userId) {
      return (this.userId = userId);
    },
    setDefaultProjectId(defaultProjectId) {
      this.defaultProjectId = defaultProjectId;
    },
    setCurrentProject(currentProject) {
      this.currentProject = currentProject;
    },
    setProjects(projects) {
      this.projects = projects;
    },
    setPermissions(permissions) {
      this.permissions = permissions;
    },
    setUserInfo(info) {
      this.info = info;
    },
    // 登录
    async login(userInfo) {
      try {
        const response = await login(userInfo);
        const { result, code } = response;
        if (code === ResultEnum.SUCCESS) {
          const ex = 7 * 24 * 60 * 60 * 1000; // token expiry in one week
          storage.set(ACCESS_TOKEN, result.token, ex);
          storage.set(CURRENT_USER, result, ex);
          // storage.set(IS_LOCKSCREEN, false);
          localStorage.setItem(CSRF_TOKEN, result.csrfToken); // Store CSRF token
          this.setToken(result.token);
          this.setUserInfo(result);
        }
        return Promise.resolve(response);
      } catch (e) {
        return Promise.reject(e);
      }
    },

    // 获取用户信息
    GetInfo() {
      return new Promise((resolve, reject) => {
        const currentProject = sessionStorage.getItem(CURRENT_PROJECT);
        const params = {};
        if (!!currentProject) {
          Object.assign(params, {
            id: JSON.parse(currentProject).id,
          });
        }
        getUserInfo(params)
          .then((res) => {
            const result = res;
            if (!!result.is_navidata_staff || (result.permissions && result.permissions.length)) {
              const permissionsList = result.permissions;
              if(!!result.is_navidata_staff) permissionsList.push({value:'is_navidata_staff'});
              this.setPermissions(permissionsList);
            } else {
              // The backend answers 403 for this now, so this only catches an older
              // backend. Either way the message has to be readable: rejecting with a
              // developer string used to leave the user on a blank loading screen,
              // because the router guard never completed the navigation.
              reject(
                Object.assign(
                  new Error(
                    'No project permissions are assigned to your account. Please contact your administrator.',
                  ),
                  { showDialog: true },
                ),
              );
              return;
            }
            !!result.defaultProjectId && this.setDefaultProjectId(result.defaultProjectId);
            // The server falls back to another project when the one we asked for is no
            // longer accessible, so trust its answer over our cached id - otherwise the
            // header keeps offering a project this user can no longer open.
            if (!!result.currentProject) {
              this.setCurrentProject(result.currentProject);
              // Same slim shape the header caches - role_json has no business in storage.
              const { id, name, siteId, siteName } = result.currentProject;
              sessionStorage.setItem(CURRENT_PROJECT, JSON.stringify({ id, name, siteId, siteName }));
            }
            !!result.projects && this.setProjects(result.projects);
            !!result && this.setUserInfo(result);
            !!result.avatar && this.setAvatar(result.avatar);
            !!result.username && this.setUserame(result.username);
            !!result.firstName && this.setFirstName(result.firstName);
            !!result.lastName && this.setLastName(result.lastName);
            !!result.nickname && this.setNickame(result.nickname);
            !!result.phoneNumber && this.setPhoneNumber(result.phoneNumber);
            !!result.faxNumber && this.setFaxNumber(result.faxNumber);
            !!result.jobTitle && this.setJobTitle(result.jobTitle);
            !!result.userId && this.setUserId(result.userId);
            !!result.is_navidata_staff && this.setUserId(result.is_navidata_staff);
            resolve(res);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },

    // 登出
    async logout() {
      this.setPermissions([]);
      this.setUserInfo('');
      storage.remove(ACCESS_TOKEN);
      storage.remove(CURRENT_USER);
      return Promise.resolve('');
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWidthOut() {
  return useUserStore(store);
}
